import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {defaultOptions, pdfOptions, responseToObject} from '@emma-labs/shared/angular';
import {
  Coverage,
  DocumentName,
  ENVIRONMENT,
  EnvironmentBroker,
  MinifiedSaleReport,
  PaginatedResponse,
  PaginationParams,
  SaleReport,
} from '@emma-labs/shared/typescript';
import {catchError, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {handleHttpError} from '../../utils/utility-constants';
import {AuthService} from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class SaleReportApiService {
  private readonly basePath: string;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentBroker,
  ) {
    this.basePath = `${environment.msSaleApiBase}/sale-reports`;
  }

  search(
    sessionId: string,
    filter: string,
    excludeExternal: boolean,
    excludeOldGen: boolean,
  ): Observable<SaleReport[]> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/search`);
    url.searchParams.set('filter', filter);
    if (excludeExternal) {
      url.searchParams.set('excludeExternal', 'true');
    }
    if (excludeOldGen) {
      url.searchParams.set('excludeOldGen', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map(
        (response) =>
          responseToObject<{saleReports: SaleReport[]}>(response as HttpResponse<string>).saleReports,
      ),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getAll(
    sessionId: string,
    excludeSelfServe: boolean,
    excludeExternal: boolean,
    excludeInactiveProviders: boolean,
  ): Observable<MinifiedSaleReport[]> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(this.basePath);

    if (excludeSelfServe) {
      url.searchParams.set('excludeSelfServe', 'true');
    }
    if (excludeExternal) {
      url.searchParams.set('excludeExternal', 'true');
    }
    if (excludeInactiveProviders) {
      url.searchParams.set('excludeInactiveProviders', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map(
        (response) =>
          responseToObject<{
            saleReports: MinifiedSaleReport[];
          }>(response as HttpResponse<string>).saleReports,
      ),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getAllPaginated(
    sessionId: string,
    params: PaginationParams,
    excludeSelfServe: boolean,
    excludeExternal: boolean,
    excludeInactiveProviders: boolean,
  ): Observable<PaginatedResponse<MinifiedSaleReport>> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/paginated`);

    url.searchParams.set('page', params.page.toString());
    url.searchParams.set('limit', params.limit.toString());

    if (params.search) {
      url.searchParams.set('search', params.search);
    }

    if (params.advisorFilter) {
      url.searchParams.set('advisorFilter', params.advisorFilter);
    }

    if (params.statusFilter && params.statusFilter.length > 0) {
      url.searchParams.set('statusFilter', params.statusFilter.join(','));
    }

    if (excludeSelfServe) {
      url.searchParams.set('excludeSelfServe', 'true');
    }
    if (excludeExternal) {
      url.searchParams.set('excludeExternal', 'true');
    }
    if (excludeInactiveProviders) {
      url.searchParams.set('excludeInactiveProviders', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map((response) =>
        responseToObject<PaginatedResponse<MinifiedSaleReport>>(response as HttpResponse<string>),
      ),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  getDocument(id: string, saleReportId: string, documentName: DocumentName): Observable<Blob> {
    const options = pdfOptions(id, this.environment.app).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(saleReportId)}/documents/${documentName}`);

    return this.http.get(url.toString(), {...options, responseType: 'blob'}).pipe(
      map((res) => res.body as Blob),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  sendModificationNotice(sessionId: string, saleReportId: string): Observable<SaleReport> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(saleReportId)}/modification-notice`);

    return this.http.post(url.toString(), {}, options).pipe(
      map((response) => responseToObject<SaleReport>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  sendClientSessionAccessLink(sessionId: string, saleReportId: string): Observable<undefined> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(saleReportId)}/client-access-link`);

    return this.http.post(url.toString(), {}, options).pipe(
      map(() => undefined),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }

  updateProtection(
    sessionId: string,
    saleReportId: string,
    coverage: Coverage,
    versionHash?: string,
  ): Observable<SaleReport> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(saleReportId)}/protection`);

    return this.http.put(url.toString(), coverage, options).pipe(
      map((response) => responseToObject<SaleReport>(response as HttpResponse<string>)),
      catchError((error) => handleHttpError(error, () => this.authService.handleLogout())),
    );
  }
}
